import { useEffect, useState } from 'react'
import { projectsApi } from '../lib/api'
import type { Project } from '../types'
import { ProjectCard } from '../components/ProjectCard'

export function Projects() {
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [showNewModal, setShowNewModal] = useState(false)
  const [newProjectName, setNewProjectName] = useState('')
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    projectsApi
      .getAll()
      .then(setProjects)
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newProjectName.trim()) return

    setCreating(true)
    try {
      const project = await projectsApi.create({ name: newProjectName })
      setProjects((prev) => [project, ...prev])
      setShowNewModal(false)
      setNewProjectName('')
    } catch (error) {
      console.error(error)
    } finally {
      setCreating(false)
    }
  }

  return (
    <>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div className="flex flex-col gap-2">
          <h1 className="text-3xl md:text-4xl font-semibold text-text-main leading-tight">
            My Projects
          </h1>
          <p className="text-text-muted text-base">
            Organize your product photos into projects
          </p>
        </div>
        <button
          onClick={() => setShowNewModal(true)}
          className="btn btn-primary"
        >
          <span className="material-symbols-outlined text-[20px]">add</span>
          <span>New Project</span>
        </button>
      </div>

      {/* Projects Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="aspect-[4/3] rounded-2xl bg-surface-muted animate-pulse"
            />
          ))}
        </div>
      ) : projects.length === 0 ? (
        <div className="text-center py-16">
          <span className="material-symbols-outlined text-6xl text-text-light mb-4 block">
            folder_open
          </span>
          <h3 className="text-lg font-medium mb-2">No projects yet</h3>
          <p className="text-text-muted mb-6">
            Create your first project to organize your photos
          </p>
          <button
            onClick={() => setShowNewModal(true)}
            className="btn btn-primary"
          >
            <span className="material-symbols-outlined text-lg">add</span>
            Create Project
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {projects.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </div>
      )}

      {/* New Project Modal */}
      {showNewModal && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setShowNewModal(false)}
          />
          <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white rounded-2xl shadow-hover z-50 p-6">
            <h3 className="font-serif text-xl font-semibold mb-4">
              Create New Project
            </h3>
            <form onSubmit={handleCreateProject}>
              <input
                type="text"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                placeholder="Project name"
                className="input mb-4"
                autoFocus
              />
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowNewModal(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating || !newProjectName.trim()}
                  className="btn btn-primary"
                >
                  {creating ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </>
  )
}
